import { useEffect, useRef, useState } from 'react';

export const DEFAULT_SCROLL_SHADOW_THRESHOLD = 10;

interface UseScrollShadowOptions {
  /** Scrollable viewport element */
  scrollElement: HTMLElement | null;
  /**
   * Optional inner content wrapper. Observing it catches `scrollHeight` changes when the
   * viewport’s pixel size is unchanged (e.g. fixed max-height).
   */
  contentElement?: HTMLElement | null;
  /** Pixel threshold for treating scroll position as “at top” / “at bottom”. */
  threshold?: number;
  /**
   * Optional bump when list/content identity changes without a guaranteed resize event
   * (e.g. item count). Forces a fresh measurement.
   */
  measureRevision?: string | number;
}

interface UseScrollShadowReturn {
  /** More content below the visible area */
  showBottomShadow: boolean;
  /** More content above the visible area */
  showTopShadow: boolean;
}

/**
 * Detects scroll position for scroll-edge affordances (shadows/fades).
 * Uses passive scroll listeners, ResizeObserver on the scroll box and optional content node,
 * and coalesces scroll-driven updates to at most once per animation frame.
 */
export function useScrollShadow({
  scrollElement,
  contentElement = null,
  threshold = DEFAULT_SCROLL_SHADOW_THRESHOLD,
  measureRevision,
}: UseScrollShadowOptions): UseScrollShadowReturn {
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const scrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!scrollElement) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      const isAtTop = scrollTop <= threshold;

      setShowBottomShadow(isScrollable && !isAtBottom);
      setShowTopShadow(isScrollable && !isAtTop);
    };

    const scheduleCheckFromScroll = () => {
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        checkScroll();
      });
    };

    checkScroll();

    scrollElement.addEventListener('scroll', scheduleCheckFromScroll, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });
    resizeObserver.observe(scrollElement);
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }

    return () => {
      scrollElement.removeEventListener('scroll', scheduleCheckFromScroll);
      resizeObserver.disconnect();
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [scrollElement, contentElement, threshold, measureRevision]);

  return { showBottomShadow, showTopShadow };
}
